import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { useGetCurrentDebateQuery } from 'redux/api/debate.api';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';
import { STREAM_ENDING } from 'shared/constants/modal-constants';
import { useNotFoundRedirect } from 'shared/guards/useNotFoundRedirect';
import { EDebateStatuses, ERoundStatuses } from 'shared/types/debates.interface';
import { urls } from 'shared/constants/routing.constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { TModalIcons, EModalsNames, EModalIcons } from 'shared/types/modal.interface';
import VideoJSPlayer from 'components/VideoJSPlayer/VideoJSPlayer';
import Box from '@mui/material/Box';
import DebateSkeleton from 'components/Skeletons/DebateSkeleton/DebateSkeleton';
import { PageContainer } from 'pages/PageContainer';
import { FullCardDescription } from 'pages/Main/FullCard/FullCardDescription/FullCardDescription';
import { ButtonShare } from 'components/Button/ButtonShare/ButtonShare';
import { RoundProgress } from 'components/RoundProgress/RoundProgress';
import { useRemainingTime } from 'shared/hooks/useRemainingTime';
import { EPageTypes } from 'shared/types/common.interface';
import { readingBaseURL } from 'shared/constants/stream-constants';

/**
 * This is a Stream page. It gets debateId from localStorage, and then loads a stream player
 * which loads the corresponding stream by the debateId.
 * @returns loaded player
 */
const Stream = () => {
  // getting the debateId from localStorage
  const debateId = localStorage.getItem(LOCAL_STORAGE.currentDebateId);
  const hlsStreamURL = `${readingBaseURL}/${debateId}/index.m3u8`;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    data: currentDebate = null,
    isLoading,
    error: currentDebateError,
  } = useGetCurrentDebateQuery(debateId, {
    pollingInterval: 1000,
    refetchOnMountOrArgChange: true,
    skip: !debateId,
  });

  useNotFoundRedirect(currentDebateError, !debateId);
  useRemainingTime();

  const leaveStream = useCallback(
    (message?: string, icon?: TModalIcons) => {
      navigate(`${urls.commonPages.debateCard}/${currentDebate?.id}`);
      if (message && icon) {
        dispatch(
          openCurrentModal({
            popupName: EModalsNames.PopUpNotification,
            data: { title: message, icon },
          }),
        );
      }
      localStorage.removeItem(LOCAL_STORAGE.currentDebateId);
    },
    [navigate, currentDebate, dispatch],
  );

  const currentRound = useMemo(
    () => currentDebate?.rounds.find((round) => round.status === ERoundStatuses.Active) ?? null,
    [currentDebate?.rounds],
  );

  useEffect(() => {
    if (!currentDebate) return;
    localStorage.setItem(LOCAL_STORAGE.currentDebateTitle, currentDebate.challenge.thesis);

    switch (currentDebate?.status) {
      case EDebateStatuses.Finished:
        leaveStream(STREAM_ENDING.finished, EModalIcons.warning);
        break;
      case EDebateStatuses.Left:
        leaveStream(STREAM_ENDING.left, EModalIcons.warning);
        break;
      case EDebateStatuses.Failed:
        leaveStream(STREAM_ENDING.failed, EModalIcons.error);
        break;
      default:
        break;
    }
  }, [currentDebate, leaveStream]);

  if (isLoading || !currentDebate) {
    return (
      <PageContainer>
        <DebateSkeleton type={EPageTypes.debate} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box width="100%" alignSelf="center">
        <VideoJSPlayer source={{ src: hlsStreamURL }} />
        <RoundProgress
          currentRound={currentRound}
          totalRoundsNumber={currentDebate.challenge.debate_settings.rounds_per_participant}
          isParticipantActive
          mt="16px"
        />
        <FullCardDescription
          challengeData={currentDebate.challenge}
          debateData={currentDebate}
          mt={{ lg: '80px', xs: '16px' }}
        >
          <ButtonShare
            shareUrl={`${window.location.origin}${urls.commonPages.debateCard}/${debateId}`}
            title={currentDebate.challenge.thesis}
          />
        </FullCardDescription>
      </Box>
    </PageContainer>
  );
};

export default Stream;
